import { Box, Grid, Heading, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { PlaybackButton, SectionHead } from "@/components";
import { HOW_HEAD, STEPS, STEPS_LABEL } from "@/content";
import { useStepCarousel } from "@/hooks";

export const HowItWorks = () => {
  const [index, setIndex] = useState(0);
  const { containerRef, videoRef, progress, autoAdvances, isPlaying, togglePlayback, stop, handleLoadedMetadata } =
    useStepCarousel({
      count: STEPS.length,
      index,
      onAdvance: setIndex,
    });
  const activeStep = STEPS[index];

  const handleChange = useCallback(
    (next: number) => {
      stop();
      setIndex(next);
    },
    [stop],
  );

  return (
    <Box as="section" id="how" maxW="maxw" mx="auto" px="pad" pb="sec">
      <SectionHead head={{ ...HOW_HEAD }} />

      <Tabs variant="steps" orientation="vertical" index={index} onChange={handleChange}>
        <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap="clamp(32px, 4vw, 64px)" alignItems="start">
          <TabList aria-label={STEPS_LABEL}>
            {STEPS.map((step, stepIndex) => {
              const isActive = stepIndex === index;

              return (
                <Tab key={step.id}>
                  <Box
                    as="span"
                    fontSize="clamp(1rem, 1.4vw, 1.375rem)"
                    color={isActive ? "brand.600" : "warm.600"}
                    transitionProperty="color"
                    transitionDuration="0.3s"
                    transitionTimingFunction="brand"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {step.index}
                  </Box>

                  <Box as="span" display="block" minW={0}>
                    <Heading
                      as="span"
                      variant="step"
                      display="block"
                      color={isActive ? "ink.DEFAULT" : "warm.600"}
                      transitionProperty="color"
                      transitionDuration="0.3s"
                      transitionTimingFunction="brand"
                    >
                      {step.title}
                    </Heading>
                    <Box
                      className="print-expand"
                      display="grid"
                      gridTemplateRows={isActive ? "1fr" : "0fr"}
                      mt={isActive ? "12px" : 0}
                      opacity={isActive ? 1 : 0}
                      transitionProperty="grid-template-rows, opacity, margin-top"
                      transitionDuration="0.4s"
                      transitionTimingFunction="brand"
                    >
                      <Box
                        overflow="hidden"
                        fontSize="body"
                        lineHeight={1.35}
                        color="warm.600"
                        maxW="46ch"
                        textTransform="none"
                        letterSpacing="normal"
                        sx={{ textWrap: "pretty" }}
                      >
                        {step.body}
                      </Box>
                    </Box>
                  </Box>
                </Tab>
              );
            })}
          </TabList>

          <Box ref={containerRef} position={{ xl: "sticky" }} top={{ xl: "104px" }}>
            <Box
              className="print-hide"
              position="relative"
              borderRadius="lg"
              overflow="hidden"
              bg="surface.white"
              border="1px solid"
              borderColor="line.hairline"
              sx={{ aspectRatio: "2 / 1" }}
            >
              <TabPanels h="100%">
                {STEPS.map((step, stepIndex) => (
                  <TabPanel key={step.id} h="100%" p={0}>
                    {stepIndex === index && (
                      <video
                        key={activeStep.id}
                        ref={videoRef}
                        src={activeStep.video.src}
                        poster={activeStep.video.poster}
                        width={activeStep.video.width}
                        height={activeStep.video.height}
                        aria-label={activeStep.alt}
                        muted
                        loop
                        playsInline
                        preload="none"
                        onLoadedMetadata={handleLoadedMetadata}
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    )}
                  </TabPanel>
                ))}
              </TabPanels>

              {autoAdvances && (
                <Box aria-hidden="true" position="absolute" left={0} right={0} bottom={0} h="3px" bg="line.glassBorder">
                  <Box h="100%" w={`${progress * 100}%`} bg="brand.500" />
                </Box>
              )}

              <PlaybackButton isPlaying={isPlaying} onToggle={togglePlayback} />
            </Box>
          </Box>
        </Grid>
      </Tabs>
    </Box>
  );
};
